import { useEffect, useState, useMemo } from 'react';
import { Sliders, PlusSquare, File } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchClients, fetchClientById } from '../slices/clientSlice';
import { fetchOrderById } from '../slices/ordersSlice';
import ClientDetails from '../components/clients/ClientDetails';
import OrderDetails from '../components/OrderDetails';
import { ArrowRightCircle } from 'react-feather';
import ClientHubCreateClientPanel from '../components/clientHub/ClientHubCreateClientPanel';
import NewClient from '../components/clients/NewClient';
import { formatPhoneNumber, removePhoneFormatting } from '../hooks/phoneFormat';
import { fetchSelf } from '../slices/userSlice';

import BncLogoSpinner from '../components/BncLogoSpinner';
import InfoTooltip from '../components/common/InfoTooltip';

export default function ClientsDashboard({ isModern, externalOrders }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	// ✅ FIX 1: Corrected the selector to access the clients array properly.
	const clients = useSelector((state) => state.clients.items);
	const clientById = useSelector((state) => state.clients.clientById);
    const currentOrder = useSelector((state) => state.orders.order);

	const [selectedClient, setSelectedClient] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
	const [showCreateClientPanel, setShowCreateClientPanel] = useState(false);
	// Search bar state...
	const [filterFirstName, setFilterFirstName] = useState('');
	const [filterLastName, setFilterLastName] = useState('');
	const [filterCompanyName, setFilterCompanyName] = useState('');
	const [filterPhone, setFilterPhone] = useState('');
	const [filterStatus, setFilterStatus] = useState('');
	const [activeTab, setActiveTab] = useState('new'); // 'new' or 'updated'

	const [currentPage, setCurrentPage] = useState(1);
	const [filterSection, setFilterSections] = useState({ filterOptions: false });
	const [pageSize, setPageSize] = useState(500);
	const [isLoading, setIsLoading] = useState(true);

	const clientIdString = localStorage.getItem('clientId');
	const clientId = clientIdString ? JSON.parse(clientIdString)?.id : null;

	// Logic for Dashboard Tabs
	const newLeads = useMemo(() => {
		if (externalOrders) {
            // If orders are provided (Dashboard mode), show recent Quotes/Leads orders
            return externalOrders
                .filter((order) => order.status === 1) // 1 = Quote
                .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
                .slice(0, 10);
        }
        // Fallback to clients if no orders provided
		if (!clients) return [];
		return clients
			.filter((client) => client.status === 1)
			.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
			.slice(0, 10);
	}, [clients, externalOrders]);

	const activeOrdersList = useMemo(() => {
		if (externalOrders) {
            return externalOrders
                .filter(order => order.status > 1 && order.status < 10) // Active: 2, 3, 4, 5... (excluding 10=Done, -1=Canceled)
                .sort((a, b) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime())
                .slice(0, 10);
        }
        return [];
	}, [externalOrders]);

	const recentlyUpdated = useMemo(() => {
		if (!clients) return [];
		return clients
			.map((client) => {
				// Prepare a "last interaction" date for sorting
				let lastInteractionTime = 0;
				let lastNote = null;

				// Check notes
				if (client.notes && client.notes.length > 0) {
					const sortedNotes = [...client.notes].sort(
						(a, b) => new Date(b.createdAt || b.CreatedAt).getTime() - new Date(a.createdAt || a.CreatedAt).getTime()
					);
					lastNote = sortedNotes[0];
					lastInteractionTime = new Date(lastNote.createdAt || lastNote.CreatedAt).getTime();
				}

				// Check updatedAt - if it's more recent than the last note (or if no notes), use it
				const updatedTime = (client.updatedAt || client.UpdatedAt) ? new Date(client.updatedAt || client.UpdatedAt).getTime() : 0;
                const createdTime = (client.createdAt || client.CreatedAt) ? new Date(client.createdAt || client.CreatedAt).getTime() : 0;
				
                // Use the most recent of note, update, or creation
				if (updatedTime > lastInteractionTime) {
					lastInteractionTime = updatedTime;
				}
                if (lastInteractionTime === 0 && createdTime > 0) {
                    lastInteractionTime = createdTime;
                }

				return { ...client, lastNote, lastInteractionTime };
			})
			// Filter out items that have no update time at all (optional, but keeps list clean)
			.filter(item => item.lastInteractionTime > 0)
			.sort((a, b) => b.lastInteractionTime - a.lastInteractionTime)
			.slice(0, 10);
	}, [clients]);

	useEffect(() => {
		setTimeout(() => {
			setIsLoading(false);
		}, 1700);
	}, []);

	// ✅ FIX 2: Correctly dispatch only one action to fetch the list of clients.
	// We should only fetch a specific client when one is selected.
	useEffect(() => {
		dispatch(
			fetchClients({
				pageSize,
				page: currentPage,
				sortOrder: 'DESC',
			})
		);
	}, [dispatch, pageSize, currentPage]);

	useEffect(() => {
		// Only run this if a clientId was found in localStorage
		if (clientId) {
			setSelectedClient(clientId);
			dispatch(fetchClientById(clientId));
		}
	}, [dispatch, clientId]);

	const openClient = (clientIdToOpen) => {
		if (clientIdToOpen) {
			setSelectedClient(clientIdToOpen);
			dispatch(fetchClientById(clientIdToOpen));
		}
	};
    
    // Helper to open order if needed
    const handleRowClick = (item) => {
        if ((activeTab === 'new' || activeTab === 'active') && externalOrders) {
             // It's an order
             if (item.id) {
                 setSelectedOrder(item.id);
                 dispatch(fetchOrderById(item.id));
                 if (item.clientId) {
                    dispatch(fetchClientById(item.clientId));
                 }
             }
        } else {
             openClient(item.id);
        }
    };

	if (isLoading) {
		return <BncLogoSpinner size={80} />;
	}

	const closeDetails = (clientIdOverride) => {
		setShowCreateClientPanel(false);
        setSelectedOrder(null);
		const resolvedClientId =
			clientIdOverride || JSON.parse(localStorage.getItem('clientId') || '{}')?.id;
		if (resolvedClientId) {
			setSelectedClient(resolvedClientId);
			dispatch(fetchClientById(resolvedClientId));
		}
	};

	const closeSlide = () => {
		setShowCreateClientPanel(false);
		setSelectedClient(null);
		localStorage.removeItem('clientId');
	};

	const applyFilters = () => {
		const newPhone = removePhoneFormatting(filterPhone);
		dispatch(
			fetchClients({
				pageSize: pageSize,
				page: currentPage,
				firstName: filterFirstName,
				lastName: filterLastName,
				companyName: filterCompanyName,
				phone: newPhone,
				status: filterStatus,
			})
		);
	};

	const resetFilters = () => {
		setFilterFirstName('');
		setFilterLastName('');
		setFilterCompanyName('');
		setFilterPhone('');
		setFilterStatus('');
		dispatch(fetchClients({ pageSize, page: currentPage, sortOrder: 'DESC' }));
	};

	const toggleSection = (section) => {
		setFilterSections((prevSections) => ({
			...prevSections,
			[section]: !prevSections[section],
		}));
	};

	const handleNewClient = async () => {
		setShowCreateClientPanel(true);
	};

    const handleClientReady = async (clientId) => {
        setShowCreateClientPanel(false);
        if (clientId) {
            setSelectedClient(clientId);
            dispatch(fetchClientById(clientId));
        }
        dispatch(fetchClients({ pageSize: 1000, page: 1, sortOrder: 'DESC' }));
    };


	// Handle both original and modern view
	if (isModern) {
		return (
			<div className="modern-table-view">
				<div className="table-header flex justify-between items-center mb-6">
					<div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
						<button 
							className={`px-6 py-2 rounded-lg font-semibold transition-all ${activeTab === 'new' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
							onClick={() => setActiveTab('new')}
						>
							{externalOrders ? 'New Leads (Quotes)' : 'New Leads'}
						</button>
						<button 
							className={`px-6 py-2 rounded-lg font-semibold transition-all ${activeTab === 'active' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
							onClick={() => setActiveTab('active')}
						>
							Active Orders
						</button>
					</div>
					<div className="flex gap-3">
						<button 
							className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg font-semibold hover:bg-slate-200 transition-colors text-sm"
							onClick={() => toggleSection('filterOptions')}
						>
							<Sliders size={16} /> Filter
						</button>
						<button 
							className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors text-sm"
							onClick={handleNewClient}
						>
							<PlusSquare size={16} /> Add New Client
						</button>
					</div>
				</div>

				{filterSection.filterOptions && (
					<div className="flex flex-wrap gap-4 mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
						<input
							type="text"
							placeholder="First name..."
							className="px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 outline-none flex-1"
							value={filterFirstName}
							onChange={(e) => setFilterFirstName(e.target.value)}
						/>
						<input
							type="text"
							placeholder="Last name..."
							className="px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 outline-none flex-1"
							value={filterLastName}
							onChange={(e) => setFilterLastName(e.target.value)}
						/>
						<button onClick={applyFilters} className="px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900">
							Search
						</button>
						<button onClick={resetFilters} className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300">
							Reset
						</button>
					</div>
				)}

				<table className="modern-table">
					<thead>
						<tr>
							<th className="flex items-center gap-2">
                                {(activeTab === 'new' || activeTab === 'active') && externalOrders ? 'Client / Order Info' : 'Client Name'}
                                {(activeTab === 'new' || activeTab === 'active') && <InfoTooltip text="Client name, order reference, and contact info." />}
                            </th>
							<th>
                                {(activeTab === 'new' || activeTab === 'active') ? (externalOrders ? 'Amount / ID' : 'Company') : 'Latest Note'}
                                {(activeTab === 'new' || activeTab === 'active') && <InfoTooltip text="Total order amount and internal reference ID." />}
                            </th>
							<th>
                                {(activeTab === 'new' || activeTab === 'active') ? 'Status' : 'Update Date'}
                                {(activeTab === 'new' || activeTab === 'active') && <InfoTooltip text="Current status of the order." />}
                            </th>
							<th className="text-right">Action</th>
						</tr>
					</thead>
					<tbody>
						{(activeTab === 'new' ? newLeads : (activeTab === 'active' ? activeOrdersList : recentlyUpdated)).map((item) => (
							<tr key={item?.id} onClick={() => handleRowClick(item)}>
								<td>
									<div className="flex items-center gap-3">
										<div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">
											{(activeTab === 'new' && externalOrders 
                                                ? (item?.clientFirstName?.[0] || 'C') 
                                                : (item?.firstName?.[0] || '') + (item?.lastName?.[0] || ''))}
										</div>
										<div>
											<div className="font-semibold text-slate-800 text-sm">
												{(activeTab === 'new' || activeTab === 'active') && externalOrders 
                                                    ? item?.clientName || `${item?.clientFirstName || ''} ${item?.clientLastName || ''}`.trim() || 'Unknown Client' 
                                                    : `${item?.firstName} ${item?.lastName}`}
											</div>
											<div className="text-xs text-slate-500">
                                                {(activeTab === 'new' || activeTab === 'active') && externalOrders 
                                                    ? `Created: ${new Date(item?.createdAt).toLocaleDateString()}`
                                                    : formatPhoneNumber(item?.phoneNumber)}
                                            </div>
										</div>
									</div>
								</td>
								<td className="max-w-xs">
									{(activeTab === 'new' || activeTab === 'active') ? (
                                        externalOrders ? (
                                            <div className="flex flex-col">
                                                <span className="font-medium text-slate-700">Order #{item?.reference || item?.id?.slice(0, 8)}</span>
                                                <span className="text-xs text-slate-500">${item?.totalAmount || '---'}</span>
                                            </div>
                                        ) : (
										    <span className="text-slate-600 font-medium text-sm">
											    {item?.companyName || '---'}
										    </span>
                                        )
									) : (
										<div className="text-sm text-slate-600 line-clamp-1 italic" title={item?.lastNote?.text}>
											{item?.lastNote?.text ? `"${item?.lastNote?.text}"` : `Updated: ${new Date(item.lastInteractionTime).toLocaleDateString()}`}
										</div>
									)}
								</td>
								<td>
									{(activeTab === 'new' || activeTab === 'active') ? (
										<span className={`status-pill ${item.status === 1 ? 'status-pending' : (item.status > 1 && item.status < 10) ? 'status-active' : 'status-blocked'}`}>
											{item.status === 1 ? (externalOrders ? 'Quote' : 'New') : (item.status > 1 && item.status < 10) ? 'Active' : (item.status === 10 ? 'Done' : 'Status')}
										</span>
									) : (
										<span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-md">
											{new Date(item.lastInteractionTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
										</span>
									)}
								</td>
								<td className="text-right">
									<button className="p-2 text-slate-400 hover:text-blue-600 transition-colors">
										<File size={20} />
									</button>
								</td>
							</tr>
						))}
                        {activeTab === 'new' && externalOrders && (
                            <tr>
                                <td colSpan="4" className="p-3 text-center">
                                    <button 
                                        onClick={() => navigate('/leads')}
                                        className="text-blue-600 font-semibold hover:text-blue-800 text-sm flex items-center justify-center gap-2 w-full"
                                    >
                                        See all leads <ArrowRightCircle size={14} />
                                    </button>
                                </td>
                            </tr>
                        )}
                        {activeTab === 'active' && externalOrders && (
                            <tr>
                                <td colSpan="4" className="p-3 text-center">
                                    <button 
                                        onClick={() => navigate('/orders')}
                                        className="text-blue-600 font-semibold hover:text-blue-800 text-sm flex items-center justify-center gap-2 w-full"
                                    >
                                        See all active orders <ArrowRightCircle size={14} />
                                    </button>
                                </td>
                            </tr>
                        )}

					</tbody>
				</table>

				{selectedClient && !selectedOrder && (
					<ClientDetails
						selectedClient={selectedClient}
						clientDetails={clientById}
						onClose={closeDetails}
						closeSlide={closeSlide}
					/>
				)}
                {selectedOrder && (
                    <OrderDetails 
                        order={currentOrder}
                        onClose={closeDetails}
                    />
                )}
				{showCreateClientPanel && (
					<div className="client-hub-overlay">
						<div className="client-hub-overlay__backdrop" />
						<div className="client-hub-overlay__panel client-hub-overlay__panel--sheet">
							<ClientHubCreateClientPanel
								clients={clients}
								currentUserId={localStorage.getItem('userId') || ''}
								onCancel={() => setShowCreateClientPanel(false)}
								onClientReady={handleClientReady}
							/>
						</div>
					</div>
				)}
			</div>
		);
	}

	return (
		<main className="dashboardList">
			<div className="searchbar">
				<div className="filter"></div>
			</div>
			{filterSection.filterOptions && (
				<div className="filters-options mt-10">
					<input
						type="text"
						placeholder="Filter by name..."
						value={filterFirstName}
						onChange={(e) => setFilterFirstName(e.target.value)}
					/>
					<input
						type="text"
						placeholder="Filter by last name..."
						value={filterLastName}
						onChange={(e) => setFilterLastName(e.target.value)}
					/>
					<input
						type="text"
						placeholder="Filter by company name..."
						value={filterCompanyName}
						onChange={(e) => setFilterCompanyName(e.target.value)}
					/>
					<button onClick={applyFilters} className="filter-btn">
						Search
					</button>
					<button onClick={resetFilters} className="filter-btn">
						Reset Inputs
					</button>
				</div>
			)}
			<div className="dashboard-content">
				<div className="action-btns-dashboard flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
					<div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
						<button 
							className={`px-4 py-1.5 rounded-lg font-semibold transition-all text-sm ${activeTab === 'new' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
							onClick={() => setActiveTab('new')}
						>
							New Leads
						</button>
						<button 
							className={`px-4 py-1.5 rounded-lg font-semibold transition-all text-sm ${activeTab === 'updated' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
							onClick={() => setActiveTab('updated')}
						>
							Recent Updates
						</button>
					</div>
					<div className="flex gap-2">
						<button
							className="dashboard-filters"
							onClick={() => toggleSection('filterOptions')}
						>
							<Sliders size={20} /> <strong>Filter</strong>
						</button>
						<button className="btn-new-users" onClick={handleNewClient}>
							<PlusSquare size={20} /> <strong>Add New Client</strong>
						</button>
					</div>
				</div>
				<table className="w-full">
					<thead className="table-header-dashboard text-left">
						<tr>
							<th className="p-3">Full Name</th>
							<th className="p-3">{activeTab === 'new' ? 'Company' : 'Latest Note'}</th>
							<th className="p-3">{activeTab === 'new' ? '' : 'Update Date'}</th>
							<th className="p-3 text-right">Details</th>
						</tr>
					</thead>
					<tbody>
						{(activeTab === 'new' ? newLeads : recentlyUpdated).map((client) => (
									<tr
										key={client?.id}
										className="select-by-name border-b border-slate-100 hover:bg-slate-50 transition-colors"
										onClick={() => openClient(client?.id)}
									>
										<td className="p-3">
											<div className="font-semibold text-slate-800">
												{client?.firstName} {client?.lastName}
											</div>
											<div className="text-xs text-slate-500">{formatPhoneNumber(client?.phoneNumber)}</div>
										</td>
										<td className="p-3 italic text-sm text-slate-600">
											{activeTab === 'new' ? (
												client?.companyName || '---'
											) : (
												<div className="line-clamp-1">
													{client?.lastNote?.text ? `"${client?.lastNote?.text}"` : `Updated: ${new Date(client.lastInteractionTime).toLocaleDateString()}`}
												</div>
											)}
										</td>
										<td className="p-3">
											{activeTab === 'updated' && (
												<span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
													{new Date(client.lastInteractionTime).toLocaleDateString()}
												</span>
											)}
										</td>
										<td className="p-3 text-right">
											<button onClick={() => openClient(client?.id)}>
												<File size={24} className="text-slate-400 hover:text-blue-600 transition-colors" />
											</button>
										</td>
									</tr>
								))}
					</tbody>
				</table>
			</div>
			{selectedClient && (
				<ClientDetails
					selectedClient={selectedClient}
					clientDetails={clientById}
					onClose={closeDetails}
					closeSlide={closeSlide}
				/>
			)}
			{showCreateClientPanel && (
				<div className="client-hub-overlay">
					<div className="client-hub-overlay__backdrop" />
					<div className="client-hub-overlay__panel client-hub-overlay__panel--sheet">
						<ClientHubCreateClientPanel
							clients={clients}
							currentUserId={localStorage.getItem('userId') || ''}
							onCancel={() => setShowCreateClientPanel(false)}
							onClientReady={handleClientReady}
						/>
					</div>
				</div>
			)}
		</main>
	);
}
